/**
 * Product page behaviour: gallery, variant selection, supply tiers with
 * per-tier add-ons, subscriptions, and the mobile sticky purchase bar.
 *
 * The <form> is a real Shopify product form that posts to /cart/add and
 * works with zero JS — in that state it adds the selected variant at the
 * selected quantity, without add-ons (which need a multi-item request). This
 * file upgrades that submit into a single AJAX call carrying the tier, its
 * variant, any ticked add-ons and the chosen selling plan.
 *
 * Cart mutation itself is NOT reimplemented here: everything goes through
 * window.themeCart from assets/cart.js, so the drawer, the /cart page and
 * the header badge stay in sync through one code path.
 *
 * Price display is shared too — updatePrices() writes the same
 * [data-price-current]/[data-price-compare] elements the buy box and the
 * sticky bar both use, so they can never disagree.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------ prices -- */

  function updatePrices(root, priceFormatted, compareFormatted) {
    root.querySelectorAll('[data-product-price]').forEach(function (el) {
      if (el.closest('.fbt')) return; /* ELORAA: başka ürünün kartı, ana fiyat yazılmaz */
      var current = el.querySelector('[data-price-current]');
      var compare = el.querySelector('[data-price-compare]');
      /* innerHTML, not textContent: Shopify's money filter output is
         HTML-safe and can legitimately contain entities such as &nbsp;
         depending on the shop's money format. Always server-rendered
         currency, never user input. */
      if (current && priceFormatted) current.innerHTML = priceFormatted;
      if (compare) {
        if (compareFormatted) {
          compare.innerHTML = compareFormatted;
          compare.hidden = false;
          el.classList.add('price--on-sale');
        } else {
          compare.hidden = true;
          el.classList.remove('price--on-sale');
        }
      }
    });
  }

  function updateAddToCartState(root, available) {
    root.querySelectorAll('[data-add-to-cart]').forEach(function (button) {
      button.disabled = !available;
      var label = button.querySelector('[data-add-to-cart-text]');
      if (!label) return;
      label.textContent = available
        ? label.dataset.availableText || label.textContent
        : label.dataset.soldOutText || label.textContent;
    });
  }

  /* ----------------------------------------------------------- gallery -- */

  function initGallery(root) {
    root.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
      if (gallery.dataset.galleryInit) return;
      gallery.dataset.galleryInit = 'true';

      var track = gallery.querySelector('[data-gallery-track]');
      var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-slide]'));
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
      if (!track || !slides.length) return;

      function scrollToIndex(index) {
        var slide = slides[index];
        if (!slide) return;
        track.scrollTo({
          left: slide.offsetLeft - track.offsetLeft,
          behavior: prefersReducedMotion.matches ? 'auto' : 'smooth'
        });
      }

      function markCurrent(index) {
        thumbs.forEach(function (thumb, i) {
          thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }

      thumbs.forEach(function (thumb, index) {
        thumb.addEventListener('click', function () {
          scrollToIndex(index);
          markCurrent(index);
        });
      });

      /* Keep the thumbnail rail honest when the visitor swipes the stage
         directly rather than using the thumbnails. */
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.intersectionRatio < 0.6) return;
              markCurrent(slides.indexOf(entry.target));
            });
          },
          { root: track, threshold: [0.6] }
        );
        slides.forEach(function (slide) { observer.observe(slide); });
      }

      /* Jump to a variant's own image. Exposed on the element so the
         variant logic below can call it without a global. */
      gallery.scrollToMediaId = function (mediaId) {
        var index = slides.findIndex(function (slide) {
          return slide.dataset.mediaId === String(mediaId);
        });
        if (index === -1) return;
        scrollToIndex(index);
        markCurrent(index);
      };

      var initial = slides.findIndex(function (slide) {
        return slide.hasAttribute('data-initial-slide');
      });
      if (initial > 0) {
        scrollToIndex(initial);
        markCurrent(initial);
      }

      /* Thumbnail rail arrows. */
      var thumbList = gallery.querySelector('[data-gallery-thumbs]');
      var prev = gallery.querySelector('[data-gallery-thumbs-prev]');
      var next = gallery.querySelector('[data-gallery-thumbs-next]');

      function updateArrows() {
        if (!thumbList) return;
        var max = thumbList.scrollWidth - thumbList.clientWidth - 1;
        if (prev) prev.disabled = thumbList.scrollLeft <= 0;
        if (next) next.disabled = thumbList.scrollLeft >= max;
      }

      if (thumbList) {
        if (prev) prev.addEventListener('click', function () {
          thumbList.scrollBy({ left: -thumbList.clientWidth * 0.7, behavior: 'smooth' });
        });
        if (next) next.addEventListener('click', function () {
          thumbList.scrollBy({ left: thumbList.clientWidth * 0.7, behavior: 'smooth' });
        });
        thumbList.addEventListener('scroll', updateArrows, { passive: true });
        window.addEventListener('resize', updateArrows);
        updateArrows();
      }

      /* Zoom dialog: clones the current slide into a native <dialog>, which
         supplies the focus trap and Escape handling. */
      var zoomButton = gallery.querySelector('[data-gallery-zoom]');
      var dialog = gallery.parentElement.querySelector('[data-gallery-zoom-dialog]');
      if (zoomButton && dialog) {
        var body = dialog.querySelector('[data-gallery-zoom-body]');
        zoomButton.addEventListener('click', function () {
          var currentIndex = thumbs.findIndex(function (thumb) {
            return thumb.getAttribute('aria-current') === 'true';
          });
          var slide = slides[currentIndex === -1 ? 0 : currentIndex];
          if (!slide || !body) return;
          body.innerHTML = '';
          body.appendChild(slide.cloneNode(true));
          if (typeof dialog.showModal === 'function') dialog.showModal();
        });
        dialog.querySelectorAll('[data-gallery-zoom-close]').forEach(function (button) {
          button.addEventListener('click', function () { dialog.close(); });
        });
        dialog.addEventListener('click', function (event) {
          if (event.target === dialog) dialog.close();
        });
      }
    });
  }

  /* --------------------------------------------------------- buy box ---- */

  function initProductForm(root) {
    root.querySelectorAll('[data-product-form]').forEach(function (form) {
      if (form.dataset.productFormInit) return;
      form.dataset.productFormInit = 'true';

      var container = form.closest('.main-product') || document;
      var jsonEl = container.querySelector('[data-product-variants-json]');
      var variants = [];
      if (jsonEl) {
        try {
          variants = JSON.parse(jsonEl.textContent);
        } catch (error) {
          variants = [];
        }
      }

      var variantInput = form.querySelector('[data-variant-id-input]');
      var quantityHidden = form.querySelector('[data-quantity-hidden]');
      var sellingPlanInput = form.querySelector('[data-selling-plan-input]');
      var gallery = container.querySelector('[data-product-gallery]');
      var quantityInput = form.querySelector('[data-quantity-input]');

      var tiers = Array.prototype.slice.call(form.querySelectorAll('[data-supply-tier]'));
      var hasTiers = tiers.length > 0;

      function findVariantByOptions(values) {
        return variants.find(function (variant) {
          return variant.optionValues.every(function (value, i) {
            return values[i] === undefined || value === values[i];
          });
        });
      }

      /* ---- standard variant picker ---- */
      var optionGroups = Array.prototype.slice.call(
        form.querySelectorAll('[data-variant-picker] [data-option-position]')
      ).sort(function (a, b) {
        return Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition);
      });

      function selectedOptionValues() {
        return optionGroups.map(function (group) {
          var checked = group.querySelector('[data-option-value]:checked');
          return checked ? checked.value : null;
        });
      }

      function refreshOptionAvailability(values) {
        optionGroups.forEach(function (group, position) {
          group.querySelectorAll('[data-option-value]').forEach(function (input) {
            var candidate = values.slice();
            candidate[position] = input.value;
            var match = findVariantByOptions(candidate);
            var label = group.querySelector('label[for="' + input.id + '"]');
            if (!label) return;
            label.classList.toggle('is-unavailable', !match);
            label.classList.toggle('is-sold-out', Boolean(match) && !match.available);
          });
        });
      }

      function applyVariant(variant) {
        if (!variant) {
          updateAddToCartState(document, false);
          return;
        }
        if (variantInput) variantInput.value = variant.id;
        updatePrices(document, variant.price, variant.compareAtPrice);
        updateAddToCartState(document, variant.available);

        if (variant.featuredMediaId && gallery && gallery.scrollToMediaId) {
          gallery.scrollToMediaId(variant.featuredMediaId);
        }

        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
      }

      optionGroups.forEach(function (group) {
        group.addEventListener('change', function (event) {
          if (!event.target.matches('[data-option-value]')) return;
          var values = selectedOptionValues();
          refreshOptionAvailability(values);
          applyVariant(findVariantByOptions(values));
          var label = group.querySelector('[data-option-selected]');
          if (label) label.textContent = event.target.value;
        });
      });

      if (optionGroups.length) refreshOptionAvailability(selectedOptionValues());

      /* ---- supply tiers ---- */
      function selectedTier() {
        return tiers.find(function (tier) {
          var radio = tier.querySelector('[data-tier-radio]');
          return radio && radio.checked;
        });
      }

      function applyTier() {
        var tier = selectedTier();
        tiers.forEach(function (item) {
          var radio = item.querySelector('[data-tier-radio]');
          item.classList.toggle('is-selected', Boolean(radio && radio.checked));
        });
        if (!tier) return;

        var radio = tier.querySelector('[data-tier-radio]');
        var variantId = radio.dataset.variantId;

        /* A tier that exposes an option as swatches resolves its own
           variant from the chosen value, so the swatch and the tier price
           always describe the same thing. */
        var optionGroup = tier.querySelector('[data-tier-option]');
        if (optionGroup) {
          var checked = optionGroup.querySelector('[data-tier-option-value]:checked');
          var position = Number(optionGroup.dataset.optionPosition) - 1;
          if (checked && !isNaN(position)) {
            var values = [];
            values[position] = checked.value;
            var match = findVariantByOptions(values);
            if (match) {
              variantId = match.id;
              var quantity = Number(radio.dataset.quantity) || 1;
              var linePrice = match.priceAmount * quantity;
              updatePrices(document, formatMoney(linePrice), null);
            }
            var selectedLabel = optionGroup.querySelector('[data-tier-option-selected]');
            if (selectedLabel) selectedLabel.textContent = checked.value;
          }
        } else {
          updatePrices(document, radio.dataset.priceFormatted, radio.dataset.compareFormatted);
        }

        if (variantInput) variantInput.value = variantId;
        if (quantityHidden) quantityHidden.value = radio.dataset.quantity || '1';
        updateAddToCartState(document, !radio.disabled);
      }

      tiers.forEach(function (tier) {
        tier.addEventListener('change', function (event) {
          /* Every tier shows its own swatches and add-ons, so touching one is
             an unambiguous signal that this is the tier being bought. Select
             it, rather than letting the shopper configure an option that would
             never reach the cart. */
          if (event.target.matches('[data-tier-option-value], [data-tier-addon]')) {
            var radio = tier.querySelector('[data-tier-radio]');
            if (radio && !radio.checked && !radio.disabled) radio.checked = true;
          }
          if (event.target.matches('[data-tier-radio], [data-tier-option-value], [data-tier-addon]')) {
            applyTier();
          }
        });
      });

      if (hasTiers) {
        /* Nothing pre-selected in the editor means the first tier wins,
           so the buy box is never in an unselectable state. */
        if (!selectedTier()) {
          var firstRadio = tiers[0].querySelector('[data-tier-radio]');
          if (firstRadio) firstRadio.checked = true;
        }
        applyTier();
      }

      /* ---- subscription ---- */
      var subscriptionRadios = form.querySelectorAll('[data-subscription-radio]');
      var planSelect = form.querySelector('[data-selling-plan-select]');

      function applySubscription() {
        var checked = form.querySelector('[data-subscription-radio]:checked');
        if (!checked || !sellingPlanInput) return;

        var isSubscription = checked.value === 'subscription';
        if (isSubscription) {
          var planId = planSelect ? planSelect.value : checked.dataset.sellingPlan;
          sellingPlanInput.value = planId || '';
          sellingPlanInput.disabled = !planId;

          if (planSelect) {
            var option = planSelect.options[planSelect.selectedIndex];
            if (option) updatePrices(document, option.dataset.priceFormatted, option.dataset.compareFormatted);
          }
        } else {
          sellingPlanInput.value = '';
          sellingPlanInput.disabled = true;
          if (hasTiers) applyTier();
        }
      }

      subscriptionRadios.forEach(function (radio) {
        radio.addEventListener('change', applySubscription);
      });
      if (planSelect) planSelect.addEventListener('change', applySubscription);
      if (subscriptionRadios.length) applySubscription();

      /* ---- quantity stepper ---- */
      function syncQuantity() {
        if (quantityInput && quantityHidden) quantityHidden.value = quantityInput.value;
      }

      var decrease = form.querySelector('[data-quantity-decrease]');
      var increase = form.querySelector('[data-quantity-increase]');
      if (decrease) decrease.addEventListener('click', function () {
        if (!quantityInput) return;
        quantityInput.value = Math.max(1, Number(quantityInput.value) - 1);
        syncQuantity();
      });
      if (increase) increase.addEventListener('click', function () {
        if (!quantityInput) return;
        quantityInput.value = Number(quantityInput.value) + 1;
        syncQuantity();
      });
      if (quantityInput) quantityInput.addEventListener('change', syncQuantity);

      /* ---- submit ---- */
      form.addEventListener('submit', function (event) {
        if (!window.themeCart) return; // cart engine unavailable — native submit

        var items = [];
        var mainItem = {
          id: Number(variantInput && variantInput.value),
          quantity: Number(quantityHidden && quantityHidden.value) || 1
        };
        if (sellingPlanInput && !sellingPlanInput.disabled && sellingPlanInput.value) {
          mainItem.selling_plan = Number(sellingPlanInput.value);
        }
        if (!mainItem.id) return; // nothing valid selected — let the browser complain
        items.push(mainItem);

        /* Add-ons belong to the SELECTED tier only. An add-on ticked inside
           a tier the shopper then moved away from must not follow them into
           the cart. */
        var tier = selectedTier();
        if (tier) {
          tier.querySelectorAll('[data-tier-addon]:checked:not(:disabled)').forEach(function (addon) {
            items.push({ id: Number(addon.dataset.variantId), quantity: 1 });
          });
        }

        event.preventDefault();

        var button = form.querySelector('[data-add-to-cart]');
        var errorEl = form.querySelector('[data-cart-error]');
        if (button) button.classList.add('is-loading');
        if (errorEl) errorEl.hidden = true;

        window.themeCart
          .add(items)
          .then(function () {
            window.themeCart.openDrawer();
          })
          .catch(function (error) {
            console.error('[product]', error);
            if (errorEl) {
              errorEl.textContent = error.message || (window.theme && window.theme.strings.cartError);
              errorEl.hidden = false;
            }
          })
          .finally(function () {
            if (button) button.classList.remove('is-loading');
          });
      });
    });
  }

  /* Formats an amount in cents using the shop's money format. Only the
     common {{amount}}-style placeholders are handled; anything else falls
     back to the server-rendered string, which is always correct. */
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    if (format.indexOf('amount_no_decimals') > -1) value = Math.round(cents / 100).toString();
    return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
  }

  /* -------------------------------------------------------- sticky bar -- */

  function initStickyBar(root) {
    root.querySelectorAll('[data-sticky-buy]').forEach(function (bar) {
      if (bar.dataset.stickyBuyInit) return;
      bar.dataset.stickyBuyInit = 'true';

      var trigger = document.querySelector('[data-add-to-cart]');
      if (!trigger || !('IntersectionObserver' in window)) return;

      /* The bar appears only once the real Add to Cart button has scrolled
         out of view, so the two are never on screen at once. */
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            bar.hidden = entry.isIntersecting; /* ELORAA: sayfa açılınca da görünsün (gerçek buton ekranda değilse) */
          });
        },
        { threshold: 0 }
      );

      observer.observe(trigger);
    });
  }

  function init(root) {
    initGallery(root);
    initProductForm(root);
    initStickyBar(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
